import type { ECharts } from "echarts/core";
import { type JSX, useCallback } from "react";
import type { PlotLayout } from "./layout/plot-layout";
import { usePanelReadout } from "./readout/readout-context";
import { useChartProjection } from "./use-chart-projection";

const LINE_COLOR = "#CDD3D0";

/** The crosshair line, parked on the readout's moment while nothing is hovered. The time pill names it. */
export function ParkedCrosshair({
    chart,
    layout,
    panelWidthPx,
    plotBottomPx,
}: {
    chart: ECharts | null;
    layout: PlotLayout;
    panelWidthPx: number;
    plotBottomPx: number;
}): JSX.Element | null {
    const { readout } = usePanelReadout();
    const parkedMs = readout.mode === "live" || readout.mode === "stale" ? readout.timeMs : null;

    const project = useCallback(
        (instance: ECharts): number | null => {
            if (parkedMs === null) return null;
            const pixel = instance.convertToPixel({ xAxisIndex: 0 }, parkedMs);
            return typeof pixel === "number" && Number.isFinite(pixel) ? pixel : null;
        },
        [parkedMs],
    );
    const x = useChartProjection(chart, project);

    const plotLeftPx = layout.gutters.left;
    const plotRightPx = panelWidthPx - layout.gutters.right;
    if (x === null || x < plotLeftPx || x > plotRightPx) return null;

    return (
        <div
            className={"pointer-events-none absolute w-px"}
            style={{
                left: Math.round(x),
                top: layout.plotTopPx,
                height: Math.max(plotBottomPx - layout.plotTopPx, 0),
                backgroundColor: LINE_COLOR,
            }}
        />
    );
}
